import { AxiosResponse } from 'axios';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { MAIN_FIELD_TYPE } from '../domain/schemas';
import { ShuOption } from '../domain/shu';
import {
  DataTransitionAfterIntroduction,
  DataTransitionAfterIntroductionData,
  DataTransitionAfterIntroductionParams,
  SettingsOptionsTransitionAfterIntroduction,
} from '../domain/transitionAfterIntroduction/types';

import {
  DataTransitionAfterIntroduction2ndRowActionCreators,
  DataTransitionAfterIntroduction2ndRowActionTypes,
  FetchDataTransitionAfterIntroduction2ndRowAction,
  SearchDataTransitionAfterIntroduction2ndRowAction,
  ToggleDataTransitionAfterIntroduction2ndRowAction,
  singleDataTransitionAfterIntroduction2ndRowSelector,
} from '../redux/server/dataTransitionAfterIntroduction2ndRow';
import { settingsOptionsTransitionAfterIntroductionSelector } from '../redux/server/settingsOptionsTransitionAfterIntroduction';
import {
  transitionAfterIntroductionReportsSearchConditionSelector,
  transitionAfterIntroductionReportsSelectedCurrentShuSelector,
  transitionAfterIntroductionReportsSettingCurrentHallsSelector,
} from '../redux/ui/transitionAfterIntroductionReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { selectShus2HallReportSearchCondition } from '../utils/shu';
import { handleErrorSaga } from './errorSaga';

/**
 * kiCode を元に検索条件を加工する
 * @param kiCode kiList の先頭の機種
 * @param currentShu 選択中の種別の情報
 * @param searchParams 導入後推移の検索条件
 * @param searchCondition 導入後推移の設定項目
 * @returns 加工された検索条件
 */
export const queryParameterToFormConditions = (
  kiCode: string,
  currentShu: ShuOption,
  currentHalls: string[],
  searchParams: DataTransitionAfterIntroductionParams,
  searchCondition: SettingsOptionsTransitionAfterIntroduction
): DataTransitionAfterIntroductionParams => {
  // デフォルトの非推移項目
  const defaultNonTransition = searchCondition.fields.nonTransition
    .filter((value) => value.isDefault)
    .map((value) => value.code);
  // デフォルトの推移項目
  const defaultTransition = searchCondition.fields.transition
    .filter((value) => value.isDefault)
    .map((value) => value.code);

  // 表示項目が指定されていない時は、デフォルトの条件を指定する
  const fields = {
    transitiveFields:
      searchParams.transitiveFields === undefined
        ? defaultTransition
        : searchParams.transitiveFields,
    nonTransitiveFields:
      searchParams.nonTransitiveFields === undefined
        ? defaultNonTransition
        : searchParams.nonTransitiveFields,
  };

  // MEMO: 元の検索条件の非推移項目が空のときは、メインフィールドの kiTushoMei が指定される
  // そのまま送ると展開行の非推移項目がズレてしまうため、該当するデータの際は非推移項目で hlMei を指定する
  if (
    fields.nonTransitiveFields.length === 1 &&
    fields.nonTransitiveFields[0] === MAIN_FIELD_TYPE.KI_TUSHO_MEI
  ) {
    fields.nonTransitiveFields = [MAIN_FIELD_TYPE.HL_MEI];
  }

  // MEMO: 現在選択中の種別を取得
  // テーブルの種別選択で変更した場合は元の検索条件に含まれていないため、改めて加工する
  const selectedShuParams = selectShus2HallReportSearchCondition([currentShu]);

  return {
    ...searchParams,
    // MEMO: 展開行は hlMei を指定
    mainField: MAIN_FIELD_TYPE.HL_MEI,
    // MEMO: kiList を kiCode のみに変更する
    ...{ kiList: [kiCode] },
    // MEMO: ForSijiritu 系のパラメータは検索条件で指定されているものを分母として渡す
    ...{
      kiListForSijiritu: searchParams.kiList,
    },
    // MEMO: テーブルの種別選択を考慮した shuList / shuGroupIds を追加する
    // まずは元の検索条件から一旦 shuList / shuGroupIds を削除
    // 選択中の種別の情報を加える
    ...selectedShuParams,
    // 選択中の店舗の情報を加える
    halls: currentHalls,
    // MEMO: 表示項目が指定されていない時は、デフォルトの条件を指定する
    ...fields,
  };
};

/**
 * 導入後推移の展開行を表示する
 * @param action Action
 */
function* searchDataTransitionAfterIntroduction2ndRowSaga(
  action: SearchDataTransitionAfterIntroduction2ndRowAction
) {
  const kiCode = action.payload.kiCode;

  // APIリクエスト前に必要な情報を取得
  const searchParams: DataTransitionAfterIntroductionParams = yield select(
    transitionAfterIntroductionReportsSearchConditionSelector
  ); // 現在の検索条件

  // APIリクエスト前に必要な設定項目を取得
  const searchCondition: SettingsOptionsTransitionAfterIntroduction =
    yield select(settingsOptionsTransitionAfterIntroductionSelector); // 設定項目

  // APIリクエスト前に必要な選択中の種別を取得
  const currentShu: ShuOption = yield select(
    transitionAfterIntroductionReportsSelectedCurrentShuSelector
  ); // 選択中の種別

  // APIリクエスト前に必要な選択中の店舗を取得
  const currentHalls: string[] = yield select(
    transitionAfterIntroductionReportsSettingCurrentHallsSelector
  ); // 選択中の店舗

  // 検索条件を加工
  const params = queryParameterToFormConditions(
    kiCode,
    currentShu,
    currentHalls,
    searchParams,
    searchCondition
  );

  // 展開行を取得する
  yield put(
    DataTransitionAfterIntroduction2ndRowActionCreators.fetchDataTransitionAfterIntroduction2ndRowAction(
      kiCode,
      params
    )
  );
}

/**
 * 導入後推移の展開行データを取得する
 * @param api AxiosInstance
 * @param action Action
 */
export function* fetchDataTransitionAfterIntroduction2ndRowSaga(
  api: Api,
  action: FetchDataTransitionAfterIntroduction2ndRowAction
) {
  const kiCode = action.payload.kiCode;
  const params = action.payload.params;

  try {
    yield put(
      DataTransitionAfterIntroduction2ndRowActionCreators.fetchDataTransitionAfterIntroduction2ndRowRequestAction(
        kiCode,
        params
      )
    );

    const response: AxiosResponse<DataTransitionAfterIntroduction> = yield call(
      api.get,
      '/data/transitionAfterIntroduction',
      buildConfig(params)
    );

    yield put(
      DataTransitionAfterIntroduction2ndRowActionCreators.fetchDataTransitionAfterIntroduction2ndRowSuccessAction(
        kiCode,
        response.data
      )
    );
  } catch (error: unknown) {
    yield put(
      DataTransitionAfterIntroduction2ndRowActionCreators.renewDataTransitionAfterIntroduction2ndRowAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 指定したQueryParameterと一致する展開行を表示・非表示する
 * @param action Action
 */
function* toggleDataTransitionAfterIntroduction2ndRowSaga(
  action: ToggleDataTransitionAfterIntroduction2ndRowAction
) {
  const kiCode = action.payload.kiCode;

  if (!kiCode) {
    throw new Error('kiCode が指定されていません。');
  }

  // queryParameterをもとに展開行を取得
  const now2ndRow: DataTransitionAfterIntroductionData | undefined =
    yield select(singleDataTransitionAfterIntroduction2ndRowSelector(kiCode));

  if (now2ndRow === undefined) {
    // 未取得の場合データを取得して表示する
    yield put(
      DataTransitionAfterIntroduction2ndRowActionCreators.searchDataTransitionAfterIntroduction2ndRowAction(
        kiCode
      )
    );
  } else {
    // 取得済みの場合はデータを削除する
    yield put(
      DataTransitionAfterIntroduction2ndRowActionCreators.hideDataTransitionAfterIntroduction2ndRowAction(
        kiCode
      )
    );
  }
}

/**
 * SEARCH_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROWがDispatchされた時の処理
 */
function* handleSearchDataTransitionAfterIntroduction2ndRowSaga() {
  yield takeEvery(
    DataTransitionAfterIntroduction2ndRowActionTypes.SEARCH_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROW,
    searchDataTransitionAfterIntroduction2ndRowSaga
  );
}

/**
 * FETCH_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROWがDispatchされた時の処理
 */
function* handleFetchDataTransitionAfterIntroduction2ndRowSaga(api: Api) {
  yield takeEvery(
    DataTransitionAfterIntroduction2ndRowActionTypes.FETCH_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROW,
    fetchDataTransitionAfterIntroduction2ndRowSaga,
    api
  );
}

/**
 * TOGGLE_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROWがDispatchされた時の処理
 */
function* handleToggleDataTransitionAfterIntroduction2ndRowSaga() {
  yield takeEvery(
    DataTransitionAfterIntroduction2ndRowActionTypes.TOGGLE_DATA_TRANSITION_AFTER_INTRODUCTION_2NDROW,
    toggleDataTransitionAfterIntroduction2ndRowSaga
  );
}

export function* dataTransitionAfterIntroduction2ndRowSagas(context: {
  api: Api;
}) {
  yield fork(handleSearchDataTransitionAfterIntroduction2ndRowSaga);
  yield fork(handleFetchDataTransitionAfterIntroduction2ndRowSaga, context.api);
  yield fork(handleToggleDataTransitionAfterIntroduction2ndRowSaga); // 展開行を表示・非表示
}
